/**
 * SupportThemesWidget — live theme cluster card for the admin queue.
 *
 * Sits above the ticket list. Pulls `GET /api/admin/support/themes?days=N`
 * which reuses the Monday-pulse aggregator. Lets Tracy see the top recurring
 * complaints over 24h / 7d / 30d windows without opening the email.
 */
import { useEffect, useState } from "react";
import { Sparkles, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const WINDOWS = [
  { value: 1, label: "24h" },
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
];

export default function SupportThemesWidget() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/support/themes?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div
      className="card p-4 bg-gradient-to-br from-violet-50 to-white border-violet-200"
      data-testid="admin-support-themes-widget"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-violet-600" />
          <div className="font-display font-extrabold text-base text-slate-900">
            Top themes
          </div>
          <span className="text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
            AI-clustered
          </span>
        </div>
        <div className="flex gap-1" data-testid="admin-support-themes-window">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => setDays(w.value)}
              className={
                "text-xs font-bold px-2.5 py-1 rounded-md transition " +
                (days === w.value
                  ? "bg-violet-600 text-white"
                  : "bg-white text-slate-700 hover:bg-violet-100 border border-slate-200")
              }
              data-testid={`admin-support-themes-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 flex items-center gap-2 py-2">
          <Loader2 size={12} className="animate-spin" /> Clustering…
        </div>
      ) : !data || (!data.themes?.length && data.total === 0) ? (
        <div className="text-xs text-slate-500 py-2">
          No tickets in this window.
        </div>
      ) : !data.themes?.length ? (
        <div className="text-xs text-slate-500 py-2">
          Not enough triaged tickets yet ({data.total} ticket{data.total === 1 ? "" : "s"}) to surface themes.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600 mb-3">
            <span><strong className="text-slate-900">{data.total}</strong> tickets</span>
            {data.urgent > 0 && (
              <span className="inline-flex items-center gap-1 text-rose-700 font-semibold">
                <AlertTriangle size={11} /> {data.urgent} urgent
              </span>
            )}
            {data.aging_count > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                <TrendingUp size={11} /> {data.aging_count} aging &gt;24h
              </span>
            )}
          </div>
          <ol className="space-y-2">
            {data.themes.map((t, i) => (
              <li
                key={`${t.theme}-${i}`}
                className="flex items-start gap-3 group"
                data-testid={`admin-support-theme-${i}`}
              >
                <div className="shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-900 truncate">{t.theme}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 shrink-0">
                      {t.count}×
                    </span>
                  </div>
                  {t.example && (
                    <div className="text-xs text-slate-500 italic truncate">{t.example}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
